import { useEffect, useState, type ReactElement } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AlertTriangle, Key, RotateCcw, XCircle } from 'lucide-react'
import type { AppDispatch, RootState } from '@/redux'
import {
  dashboardCreationActions,
  selectAvailableCredentials,
  selectCredentialPrefetchError,
  selectHarvestFailure,
} from '@/redux/slice/dashboardCreationSlice'
import { prefetchAvailableCredentialsForApp } from '@/services/DashboardChatService'
import { startHarvestThunk } from '@/services/HarvestService'
import { Button } from '@/components/ui/button'
import { HarvestModal } from '../harvest/HarvestModal'
import { ManualCredentialPasteModal } from './ManualCredentialPasteModal'

/**
 * CredentialPickerHarvestBlock — FSM gate: awaiting_credential (no existing creds)
 *
 * Mockup: designs/ai-dashboard-platform/2026-04-22-credential-picker-harvest-default-v1.html
 * Failure states:
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-closed-mid-flow-v1.html   (Screen 12)
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-no-admin-role-v1.html     (Screen 13)
 *   designs/ai-dashboard-platform/2026-04-22-harvest-modal-scrape-timeout-v1.html    (Screen 14)
 *
 * Brand guide: §17 (Harvest Modal Pattern — failure states)
 *
 * Renders:
 * - Default state: harvest card with CampMinder header, API key chip, "Log in" CTA
 * - Post-failure states: inline AI bubble with failure-specific copy per brand guide §17
 * - HarvestModal: mounted conditionally, opens on "Log in" click
 */

const CAMPMINDER_ADMIN_URL =
  'https://system.campminder.com/ui/security/apiaccess/Admin'

// ─── CampMinder logo (small variant for card header) ──────────────────────────

const CampMinderLogoSmall = () => (
  <svg width="24" height="24" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path d="M14 4L3 20h22L14 4z" fill="#2E7D32" opacity=".9" />
    <rect x="6" y="20" width="16" height="4" rx="1" fill="#1B5E20" />
    <path d="M9 12 Q14 8 19 12" stroke="#A5D6A7" strokeWidth="1.5" fill="none" />
  </svg>
)

// ─── Failure message variants ──────────────────────────────────────────────────

/**
 * Shown when the user closed the modal mid-flow (user_abandoned or user_cancelled_camp_picker).
 * Screen 12 — "harvest-modal-closed-mid-flow"
 * No warning block — conversational, not alarming. Two CTAs.
 */
interface ClosedMidFlowProps {
  onRetry: () => void
  onPasteKey: () => void
}
function ClosedMidFlowMessage({ onRetry, onPasteKey }: ClosedMidFlowProps) {
  return (
    <div
      className="flex flex-col gap-3 w-full"
      data-testid="harvest-failure-closed-mid-flow"
      role="alert"
      aria-live="assertive"
    >
      <p className="text-sm text-primary leading-relaxed">
        Looks like the CampMinder login didn&apos;t finish. Want to try again, or paste an API key
        instead?
      </p>
      <div className="flex flex-col gap-2 mt-1">
        <Button
          type="button"
          className="w-full font-semibold"
          onClick={onRetry}
          aria-label="Try connecting CampMinder again"
        >
          <RotateCcw size={15} className="mr-1.5" aria-hidden="true" />
          Try again
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onPasteKey}
          aria-label="Paste an API key you already have"
        >
          Paste an API key you already have
        </Button>
      </div>
    </div>
  )
}

/**
 * Shown when the user's CampMinder account lacks admin permissions.
 * Screen 13 — "harvest-modal-no-admin-role"
 * Amber warning block + 3-step instructions + copy-to-clipboard CTA.
 */
interface NoAdminRoleProps {
  onPasteKey: () => void
}
function NoAdminRoleMessage({ onPasteKey }: NoAdminRoleProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    const text = [
      'Steps to connect TheCampStack to CampMinder:',
      '1. Go to CampMinder → Admin → Logins and Permissions → API Access Admin.',
      '2. Connect TheCampStack from the options available.',
      '3. Once done, come back to TheCampStack and you\'ll see CampMinder in your connected apps.',
    ].join('\n')
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => { /* clipboard unavailable — leave copied=false */ })
  }

  return (
    <div
      className="flex flex-col gap-3 w-full"
      data-testid="harvest-failure-no-admin-role"
      role="alert"
      aria-live="assertive"
    >
      {/* Amber warning block */}
      <div className="flex items-start gap-2.5 p-3 bg-warning-foreground border border-warning rounded-[10px]">
        <AlertTriangle size={18} className="text-warning flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-[13px] text-warning font-medium leading-relaxed">
          Your CampMinder account doesn&apos;t have admin permissions to set up this connection.
        </p>
      </div>

      <p className="text-sm text-primary">Here&apos;s what to do:</p>

      <ol className="flex flex-col gap-2.5" aria-label="Steps to resolve admin permission issue">
        {[
          <>
            Ask your camp&apos;s CampMinder admin to log in and go to{' '}
            <strong>Admin → Logins and Permissions → API Access Admin</strong>.
          </>,
          <>
            They&apos;ll see an option there to connect TCS — they can do this setup for your camp.
          </>,
          <>
            Once they&apos;ve done it, come back here and you&apos;ll see CampMinder in your
            connected apps.
          </>,
        ].map((text, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div
              className="w-[22px] h-[22px] rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-0.5 text-white text-xs font-bold"
              aria-hidden="true"
            >
              {i + 1}
            </div>
            <p className="text-[13px] text-secondary leading-relaxed">{text}</p>
          </li>
        ))}
      </ol>

      <Button
        type="button"
        variant="outline"
        className="w-full text-sm"
        onClick={handleCopy}
        aria-label="Copy these instructions to share with your CampMinder admin"
      >
        {copied ? 'Copied!' : 'Copy instructions to share'}
      </Button>

      <p className="text-center text-[13px] text-muted-foreground">
        or{' '}
        <button
          type="button"
          className="text-accent underline hover:no-underline font-medium"
          onClick={onPasteKey}
          aria-label="Paste an API key you already have"
        >
          Paste an API key you already have
        </button>
      </p>
    </div>
  )
}

/**
 * Shown when the harvest script timed out (system failure).
 * Screen 14 — "harvest-modal-scrape-timeout"
 * Red error block + optional orphan-key warning + retry/paste CTAs.
 */
interface ScrapeTimeoutProps {
  partialSuccess: boolean
  onRetry: () => void
  onPasteKey: () => void
}
function ScrapeTimeoutMessage({ partialSuccess, onRetry, onPasteKey }: ScrapeTimeoutProps) {
  return (
    <div
      className="flex flex-col gap-3 w-full"
      data-testid="harvest-failure-scrape-timeout"
      role="alert"
      aria-live="assertive"
    >
      {/* Red error block */}
      <div className="flex items-start gap-2.5 p-3 bg-destructive-foreground border border-destructive rounded-[10px]">
        <XCircle size={18} className="text-destructive flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-[13px] text-destructive font-medium leading-relaxed">
          The connection script ran into a problem. TCS has been notified and we&apos;re fixing it.
        </p>
      </div>

      {/* Orphan-key warning — conditional on partialSuccess */}
      {partialSuccess && (
        <div
          className="flex items-start gap-2 p-3 bg-warning-foreground border border-warning rounded-lg text-xs text-warning"
          role="status"
          aria-label="Orphan API key warning"
        >
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>
            An API key named &ldquo;TheCampStack&rdquo; may have been created in your CampMinder
            account.{' '}
            <a
              href={CAMPMINDER_ADMIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline hover:no-underline"
              aria-label="Delete unused API key in CampMinder Admin — opens in new tab"
            >
              Check CampMinder Admin →
            </a>
          </span>
        </div>
      )}

      <p className="text-sm text-primary leading-relaxed">
        Harvest script needs maintenance — try again shortly or paste an API key manually.
      </p>

      <div className="flex flex-col gap-2 mt-1">
        <Button
          type="button"
          className="w-full font-semibold"
          onClick={onRetry}
          aria-label="Try connecting CampMinder again"
        >
          <RotateCcw size={15} className="mr-1.5" aria-hidden="true" />
          Try again shortly
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onPasteKey}
          aria-label="Paste an API key you already have"
        >
          Paste an API key you already have
        </Button>
      </div>
    </div>
  )
}

// ─── CredentialPickerHarvestBlock ─────────────────────────────────────────────

interface CredentialPickerHarvestBlockProps {
  /**
   * Called when the user clicks "Use an existing credential" while in the
   * gateway's "Add a new connection" path (isAddingNew === true). Resets
   * the gateway's local isAddingNew state so the gateway re-evaluates and,
   * if availableCredentials is non-empty, swaps to CredentialPickerExistingBlock.
   *
   * Undefined when the harvest block is rendered for the "no existing creds"
   * case (gateway routes here because availableCredentials.length === 0) —
   * cancelling has no meaning there.
   */
  onCancelAddNew?: () => void
}

export const CredentialPickerHarvestBlock = ({
  onCancelAddNew,
}: CredentialPickerHarvestBlockProps = {}) => {
  const dispatch: AppDispatch = useDispatch()
  const sourceAppCode = useSelector((state: RootState) => state.dashboardCreation.sourceAppCode)
  const token = useSelector((state: RootState) => state.user.token ?? '')
  const harvestFailure = useSelector(selectHarvestFailure)
  const credentialPrefetchError = useSelector(selectCredentialPrefetchError)
  const availableCredentials = useSelector(selectAvailableCredentials)

  const [modalOpen, setModalOpen] = useState(false)
  const [pasteModalOpen, setPasteModalOpen] = useState(false)
  /**
   * After the user clicks "Use an existing credential", if the re-fired
   * prefetch still returns zero credentials, surface a one-shot inline message
   * so the click feels responsive. Cleared on next prefetch attempt or modal
   * action.
   */
  const [noExistingFoundFlash, setNoExistingFoundFlash] = useState(false)

  // Close the modal whenever harvestFailure becomes non-null (covers both the
  // post-thunk failure case and the mid-flow socket failure case — Qodo Bug #3).
  useEffect(() => {
    if (harvestFailure) setModalOpen(false)
  }, [harvestFailure])

  // API key chip date: format today as MM/DD/YYYY
  const today = new Date()
  const keyDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`
  const apiKeyName = `TheCampStack ${keyDate}`

  // Source display name (for now only CampMinder)
  const sourceName = sourceAppCode === 'campminder' ? 'CampMinder' : 'your account'

  const handleLogIn = async () => {
    // Clear any prior "no existing credentials" flash — the user is choosing a
    // different recovery action (round-1 review 2026-05-21 Copilot CO2).
    setNoExistingFoundFlash(false)
    dispatch(dashboardCreationActions.clearHarvest())
    // Dispatch startHarvestThunk — sets sessionId + liveViewUrl + transitions to 'login'
    await (dispatch as (action: unknown) => Promise<void>)(
      startHarvestThunk(token, sourceAppCode ?? 'campminder')
    )
    setModalOpen(true)
  }

  const handleRetry = async () => {
    // Clear any prior "no existing credentials" flash (round-1 review CO2).
    setNoExistingFoundFlash(false)
    // Clear any prior failure and re-run the harvest thunk
    dispatch(dashboardCreationActions.clearHarvest())
    await (dispatch as (action: unknown) => Promise<void>)(
      startHarvestThunk(token, sourceAppCode ?? 'campminder')
    )
    setModalOpen(true)
  }

  const handlePasteApiKey = () => {
    // Clear any prior "no existing credentials" flash (round-1 review CO2).
    setNoExistingFoundFlash(false)
    setPasteModalOpen(true)
  }

  /**
   * "Use an existing credential" escape hatch (Bug #1 2026-05-14).
   *
   * Two flows feed this:
   *   1. Org has no creds (prefetch returned empty / failed) → harvest block
   *      shown because availableCredentials is empty. Click re-fires prefetch;
   *      if creds are found, gateway swaps to existing picker.
   *   2. Org has creds, user clicked "Add a new connection" on the existing
   *      picker → harvest block shown because isAddingNew is true. Click must
   *      ALSO reset isAddingNew (via onCancelAddNew) so the gateway re-routes
   *      to the existing picker — otherwise the prefetch's "found 3" result is
   *      ignored by the gateway's isAddingNew branch.
   *
   * onCancelAddNew is called BEFORE the prefetch so React batches the gateway's
   * state change with the post-prefetch availableCredentials change in a single
   * commit — preventing a flash of the "still on harvest block" intermediate
   * state.
   */
  const handleUseExistingCredential = async () => {
    setNoExistingFoundFlash(false)
    if (onCancelAddNew) onCancelAddNew()
    await (dispatch as (action: unknown) => Promise<void>)(
      prefetchAvailableCredentialsForApp(token, sourceAppCode ?? 'campminder')
    )
    setNoExistingFoundFlash(true)
  }

  const handlePasteSuccess = () => {
    setPasteModalOpen(false)
    // setAvailableCredentials is dispatched inside createManualCredentialThunk
  }

  const handleModalClose = () => {
    setModalOpen(false)
  }

  const handleModalSuccess = () => {
    setModalOpen(false)
  }

  // ── Failure state rendering ────────────────────────────────────────────────
  // Both modals (HarvestModal + ManualCredentialPasteModal) are rendered once
  // at the bottom of the component (regardless of which body branch is shown),
  // because their visibility is controlled by `open` props — there's no need to
  // re-mount them per branch (PR #331 Claude review §3).
  let body: ReactElement
  let showHarvestModal = true

  if (harvestFailure) {
    const code = harvestFailure.code

    if (code === 'no_admin_role') {
      body = <NoAdminRoleMessage onPasteKey={handlePasteApiKey} />
      // No-admin-role does not open the harvest modal — paste is the only path.
      showHarvestModal = false
    } else if (
      code === 'scrape_timeout' ||
      code === 'SCRAPE_TIMEOUT' ||
      code === 'selector_broken' ||
      code === 'SELECTOR_BROKEN'
    ) {
      body = (
        <ScrapeTimeoutMessage
          partialSuccess={harvestFailure.partialSuccess}
          onRetry={handleRetry}
          onPasteKey={handlePasteApiKey}
        />
      )
    } else {
      // Default failure: closed mid-flow (user_abandoned, user_cancelled_camp_picker, or any other code)
      body = <ClosedMidFlowMessage onRetry={handleRetry} onPasteKey={handlePasteApiKey} />
    }
  } else {
    // ── Default (no failure) state ─────────────────────────────────────────
    body = (
      // Narration is provided by Slice 1.3's templated chat-message
      // ("Great choice. To pull your {SourceDisplayName} data...") which is
      // persisted on the source-pick → awaiting_credential FSM transition.
      // Rendering it again here would double-narrate the same line.
      <div
        className="border-[1.5px] border-accent rounded-[12px] bg-card overflow-hidden max-w-[520px]"
        role="region"
        aria-label={`Connect ${sourceName} account`}
      >
        {/* Card header */}
        <div className="flex items-center gap-3 px-3.5 py-3 bg-accent-background border-b border-[#BFDBFE]">
          <div
            className="w-9 h-9 rounded-[8px] bg-card border border-[#BAE6FD] flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <CampMinderLogoSmall />
          </div>
          <div>
            <p className="text-sm font-semibold text-primary">Connect {sourceName}</p>
            <p className="text-xs text-secondary mt-px">Log in to set up your connection</p>
          </div>
        </div>

        {/* Card body */}
        <div className="px-3.5 py-3">
          <p className="text-sm text-secondary leading-relaxed mb-3">
            We&apos;ll create an API key named{' '}
            <span
              className="inline-flex items-center gap-1 bg-[#F0FDF4] border border-[#BBF7D0] rounded-md px-2 py-0.5 text-xs font-semibold text-[#15803D] mx-0.5"
              aria-label={`API key name: ${apiKeyName}`}
            >
              <Key size={11} aria-hidden="true" />
              {apiKeyName}
            </span>{' '}
            on your behalf (~30 seconds). You can review or revoke it anytime in {sourceName} →
            API Access Admin.
          </p>

          <Button
            type="button"
            className="w-full font-semibold text-sm"
            aria-label={`Log in to ${sourceName} to connect your account`}
            onClick={handleLogIn}
          >
            <svg
              viewBox="0 0 14 14"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              className="mr-1.5"
              aria-hidden="true"
            >
              <rect x="2" y="4" width="10" height="7" rx="1" />
              <path d="M5 4V3a2 2 0 014 0v1" />
            </svg>
            Log in to {sourceName}
          </Button>

          <button
            type="button"
            className="block w-full text-center mt-2 text-xs text-secondary hover:text-accent hover:underline transition-colors"
            aria-label="Paste an API key you already have"
            onClick={handlePasteApiKey}
          >
            Paste an API key you already have
          </button>

          {/*
            "Use an existing credential" escape hatch (Bug #1 2026-05-14).
            Always visible in the default state so users dead-ended here by a
            silent prefetch failure — or who intentionally took the "Add new"
            path from the existing picker — can attempt to load their saved
            credentials. Re-fires `prefetchAvailableCredentialsForApp`; the
            gateway swaps to CredentialPickerExistingBlock if creds are found.
          */}
          <button
            type="button"
            className="block w-full text-center mt-2 text-xs text-secondary hover:text-accent hover:underline transition-colors"
            aria-label={`Use an existing ${sourceName} credential`}
            onClick={handleUseExistingCredential}
          >
            Use an existing credential
          </button>

          {/* Prefetch failure surface — was previously a silent empty array. */}
          {credentialPrefetchError && (
            <div
              className="mt-3 px-2.5 py-2 rounded-md bg-[#FEF3C7] border border-[#FCD34D] flex items-start gap-2"
              role="status"
              aria-live="polite"
            >
              <AlertTriangle
                size={14}
                className="text-[#92400E] flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-xs text-[#92400E] leading-snug">
                We couldn&apos;t load your saved credentials. Try &quot;Use an existing
                credential&quot; again, or continue with a new connection.
              </p>
            </div>
          )}

          {/*
            One-shot flash for the re-fire-returned-empty case.

            Defensive render-time check on availableCredentials.length === 0:
            if the gateway hasn't swapped despite credentials existing (e.g.
            the re-fire returned a non-empty list but isAddingNew remained true
            because onCancelAddNew wasn't passed), this prevents the flash
            from claiming "no credentials" when there clearly are some. The
            normal happy path is: prefetch returns non-empty → gateway swaps →
            harvest unmounts → this branch never renders.
          */}
          {noExistingFoundFlash &&
            !credentialPrefetchError &&
            availableCredentials.length === 0 && (
              <p
                className="mt-2 text-center text-xs text-secondary"
                role="status"
                aria-live="polite"
              >
                No saved credentials found for {sourceName}.
              </p>
            )}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="credential-picker-harvest-block" className="flex flex-col gap-3 w-full">
      {body}

      {/* HarvestModal — mounted, open controlled by modalOpen */}
      {showHarvestModal && (
        <HarvestModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          onPasteKey={handlePasteApiKey}
        />
      )}

      {/* ManualCredentialPasteModal — inline paste flow (CEO 2026-04-28) */}
      <ManualCredentialPasteModal
        open={pasteModalOpen}
        onOpenChange={setPasteModalOpen}
        onSuccess={handlePasteSuccess}
      />
    </div>
  )
}
